import React, { useMemo, useState } from 'react';
import {
  aiSettingsApi,
  ActionCategory,
  AiAction,
  AiSettings,
  CatalogueModel,
  ChangeNote,
  ProviderKey,
} from '../../api/ai-settings';
import { useToast } from '../../hooks/useToast';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { TierBadge } from './TierBadge';
import {
  CATEGORY_HINT,
  CATEGORY_LABEL,
  eligibleModels,
  unavailableReason,
  usdPerM,
  usdSpend,
} from './format';

interface Props {
  settings: AiSettings;
  catalogue: CatalogueModel[];
  canManage: boolean;
  onChanged: () => void;
}

const muted: React.CSSProperties = { fontSize: 12, color: 'var(--neutral-600)' };
const DEFAULT_VALUE = '';

/** What a cell shows beneath its select after a change: a quiet cost warning, or a note. */
interface CellFeedback {
  warning?: string;
  estimate?: string;
  notes: ChangeNote[];
}

/**
 * The model each action uses on each provider.
 *
 * Every action has its own row and every approved vendor its own picker, so a
 * vision read can be Haiku on Anthropic and Luna on OpenAI. A picker lists
 * only models the action can use (vision actions: image-capable; classifiers
 * and extractors: tool-calling), cheapest first, each with its cost label.
 */
export const ActionsTab: React.FC<Props> = ({ settings, catalogue, canManage, onChanged }) => {
  const toast = useToast();
  const [category, setCategory] = useState<ActionCategory | 'all'>('all');
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Record<string, CellFeedback>>({});

  const providers = settings.providers.filter((p) => p.approved && p.configured);
  const byId = useMemo(() => {
    const m = new Map<string, CatalogueModel>();
    catalogue.forEach((c) => m.set(`${c.provider}:${c.modelId}`, c));
    return m;
  }, [catalogue]);

  const categories = useMemo(
    () => Array.from(new Set(settings.actions.map((a) => a.category))),
    [settings.actions],
  );
  const rows = settings.actions.filter(
    (a) =>
      (category === 'all' || a.category === category) &&
      (!query ||
        `${a.label} ${a.key} ${a.description}`.toLowerCase().includes(query.toLowerCase())),
  );

  const change = async (action: AiAction, provider: ProviderKey, value: string) => {
    const cell = `${action.key}:${provider}`;
    setSaving(cell);
    try {
      const out = await aiSettingsApi.setActionModel(
        action.key,
        provider,
        value === DEFAULT_VALUE ? null : value,
      );
      const w = out.warning;
      setFeedback((f) => ({
        ...f,
        [cell]: {
          warning: w?.message,
          estimate: w?.estimate
            ? `About ${usdSpend(w.estimate.proposedUsd)} for ${w.estimate.basis}${
                w.estimate.currentUsd != null ? `, up from ${usdSpend(w.estimate.currentUsd)}` : ''
              }.`
            : undefined,
          notes: out.notes ?? [],
        },
      }));
      toast.success('Saved. It applies within seconds.');
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not save the model.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          aria-label="Search actions"
          placeholder="Search actions"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid var(--neutral-300)', borderRadius: 'var(--radius-md)', minWidth: 220 }}
        />
        <select
          aria-label="Filter by category"
          value={category}
          onChange={(e) => setCategory(e.target.value as ActionCategory | 'all')}
          style={{ padding: '8px 12px', border: '1px solid var(--neutral-300)', borderRadius: 'var(--radius-md)' }}
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <span style={muted}>
          Spend is the last {settings.usageWindowDays} days. A model labelled High cost costs
          noticeably more per request.
        </span>
      </div>

      {rows.length === 0 && <Card>No actions match.</Card>}

      {rows.map((a) => (
        <Card key={a.key} padding={20}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>{a.label}</strong>
                <Badge variant="neutral">{CATEGORY_LABEL[a.category]}</Badge>
                {a.needs.vision && <Badge variant="info">Reads images</Badge>}
                {a.containsNames && <Badge variant="premium">Anthropic only</Badge>}
              </div>
              <div style={muted}>{a.description}</div>
              <div style={{ ...muted, color: 'var(--neutral-500)' }}>
                {a.key} · {CATEGORY_HINT[a.category]}
              </div>
            </div>
            <div style={{ ...muted, textAlign: 'right' }}>
              {a.usage
                ? `${a.usage.calls.toLocaleString('en-US')} calls · ${usdSpend(a.usage.costUsd)}`
                : 'No usage yet'}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
            {providers.map((p) => {
              const eff = a.models[p.key];
              const cell = `${a.key}:${p.key}`;
              const blocked = unavailableReason(a, p);
              const options = eligibleModels(
                catalogue,
                a,
                p.key,
                eff?.source === 'admin' ? eff.modelId : null,
              );
              const fb = feedback[cell];
              const current = eff?.source === 'admin' ? eff.modelId ?? DEFAULT_VALUE : DEFAULT_VALUE;
              const effective = eff?.modelId ? byId.get(`${p.key}:${eff.modelId}`) : undefined;
              return (
                <div key={p.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label htmlFor={cell} style={{ fontSize: 13, fontWeight: 500 }}>
                    {p.label}
                  </label>
                  <select
                    id={cell}
                    value={current}
                    disabled={!canManage || Boolean(blocked) || saving === cell}
                    onChange={(e) => change(a, p.key, e.target.value)}
                    style={{ padding: '8px 10px', border: '1px solid var(--neutral-300)', borderRadius: 'var(--radius-md)' }}
                  >
                    <option value={DEFAULT_VALUE}>
                      Default{eff?.source !== 'admin' && eff?.modelId ? ` (${eff.name ?? eff.modelId})` : ''}
                    </option>
                    {options.map((m) => (
                      <option key={m.modelId} value={m.modelId}>
                        {m.name} · {m.tier} · {usdPerM(m.inputUsdPerMTok)}/{usdPerM(m.outputUsdPerMTok)}
                        {m.active ? '' : ' (off)'}
                      </option>
                    ))}
                  </select>
                  <span style={{ ...muted, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {blocked ?? (
                      <>
                        <span>{eff?.source === 'admin' ? 'Set here' : 'Default'}</span>
                        <TierBadge tier={eff?.tier ?? effective?.tier} />
                      </>
                    )}
                  </span>
                  {fb?.warning && (
                    <span role="note" style={{ ...muted, color: 'var(--warning-900)' }}>
                      {fb.warning} {fb.estimate}
                    </span>
                  )}
                  {fb?.notes.map((n) => (
                    <span key={n.message} role="note" style={muted}>
                      {n.message}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
};
